import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { 
  Zap, RefreshCw, Heart, Gift, Cake, Plus, Edit2, Trash2, X, 
  Loader2, Save, DollarSign, MapPin, Mail, MessageSquare, Clock, Users
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OFFER_TYPES = {
  retention: { icon: RefreshCw, color: 'blue', title: 'Retention Offers', description: 'Re-engage clients who haven\'t visited recently' },
  loyalty: { icon: Heart, color: 'pink', title: 'Loyalty Offers', description: 'Reward your most loyal returning customers' },
  rescue: { icon: Gift, color: 'orange', title: 'Rescue Offers', description: 'Win back clients at risk of churning' },
  birthday: { icon: Cake, color: 'purple', title: 'Birthday Offers', description: 'Celebrate your clients\' special day' }
};

const AdminAutomatedOffers = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('retention');
  
  // Offers state
  const [offers, setOffers] = useState({ retention: [], loyalty: [], rescue: [], birthday: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Locations
  const [locations, setLocations] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '', offerType: 'retention', discountType: 'percentage', discountValue: '',
    triggerCondition: '', triggerDays: '', visitThreshold: '', messageTemplate: '',
    deliveryMethod: 'email', locationId: '', validityDays: '30', active: true
  });

  useEffect(() => {
    fetchLocations();
    fetchAllOffers();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await fetch(`${API}/locations?practiceId=demo-practice-001`);
      const data = await response.json();
      setLocations(data);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const fetchAllOffers = async () => {
    setLoading(true);
    try {
      const types = ['retention', 'loyalty', 'rescue', 'birthday'];
      const responses = await Promise.all(
        types.map(type => fetch(`${API}/automated-offers?practiceId=demo-practice-001&offerType=${type}`))
      );
      const data = await Promise.all(responses.map(r => r.json()));
      
      setOffers({
        retention: data[0],
        loyalty: data[1],
        rescue: data[2],
        birthday: data[3]
      });
    } catch (err) {
      console.error('Error fetching offers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const data = {
        ...formData,
        discountValue: parseFloat(formData.discountValue) || 0,
        triggerDays: parseInt(formData.triggerDays) || 0,
        visitThreshold: parseInt(formData.visitThreshold) || 0,
        validityDays: parseInt(formData.validityDays) || 30,
        practiceId: 'demo-practice-001'
      };

      const url = editingOffer ? `${API}/automated-offers/${editingOffer.id}` : `${API}/automated-offers`;
      const method = editingOffer ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });

      if (!response.ok) throw new Error('Failed to save offer');
      toast({ title: editingOffer ? 'Offer Updated' : 'Offer Created', description: `${formData.name} has been saved.` });
      resetForm();
      fetchAllOffers();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to save offer.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setFormData({
      name: offer.name || '', offerType: offer.offerType || 'retention',
      discountType: offer.discountType || 'percentage', discountValue: offer.discountValue?.toString() || '',
      triggerCondition: offer.triggerCondition || '', triggerDays: offer.triggerDays?.toString() || '',
      visitThreshold: offer.visitThreshold?.toString() || '', messageTemplate: offer.messageTemplate || '',
      deliveryMethod: offer.deliveryMethod || 'email', locationId: offer.locationId || '',
      validityDays: offer.validityDays?.toString() || '30', active: offer.active !== false
    });
    setShowForm(true);
  };

  const handleToggle = async (offer) => {
    try {
      await fetch(`${API}/automated-offers/${offer.id}/toggle`, { method: 'PUT' });
      toast({ title: offer.active ? 'Offer Disabled' : 'Offer Enabled' });
      fetchAllOffers();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to toggle offer.', variant: 'destructive' });
    }
  };

  const handleDelete = async (offer) => {
    if (!window.confirm(`Delete "${offer.name}"? This cannot be undone.`)) return;
    try {
      await fetch(`${API}/automated-offers/${offer.id}`, { method: 'DELETE' });
      toast({ title: 'Deleted', description: `${offer.name} has been deleted.` });
      fetchAllOffers();
    } catch (err) {
      toast({ title: 'Error', description: 'Failed to delete offer.', variant: 'destructive' });
    }
  };

  const resetForm = () => {
    setFormData({
      name: '', offerType: activeTab, discountType: 'percentage', discountValue: '',
      triggerCondition: '', triggerDays: '', visitThreshold: '', messageTemplate: '',
      deliveryMethod: 'email', locationId: '', validityDays: '30', active: true
    });
    setEditingOffer(null);
    setShowForm(false);
  };

  const openNewForm = () => {
    resetForm();
    setFormData(prev => ({ ...prev, offerType: activeTab }));
    setShowForm(true);
  };

  const getLocationName = (locationId) => {
    if (!locationId) return 'All Locations';
    const loc = locations.find(l => l.id === locationId);
    return loc ? loc.name : 'Unknown';
  };

  const getDefaultTriggerText = (type) => {
    switch (type) {
      case 'retention': return 'Days since last visit';
      case 'loyalty': return 'Number of visits';
      case 'rescue': return 'Days inactive before rescue';
      case 'birthday': return 'Days before/after birthday';
      default: return 'Trigger condition';
    }
  };

  const renderOfferCard = (offer) => {
    const typeInfo = OFFER_TYPES[offer.offerType] || OFFER_TYPES.retention;
    const Icon = typeInfo.icon;
    
    return (
      <Card key={offer.id} className={`${!offer.active ? 'opacity-60 bg-gray-50' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg bg-${typeInfo.color}-100 flex items-center justify-center`}>
                <Icon className={`h-5 w-5 text-${typeInfo.color}-600`} />
              </div>
              <div>
                <CardTitle className="text-lg">{offer.name}</CardTitle>
                <CardDescription className="flex items-center gap-1 mt-1">
                  <MapPin className="h-3 w-3" />
                  {getLocationName(offer.locationId)}
                </CardDescription>
              </div>
            </div>
            <Switch checked={offer.active} onCheckedChange={() => handleToggle(offer)} />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-2 bg-green-50 rounded">
              <span className="text-sm text-green-700">Discount</span>
              <span className="font-semibold text-green-800">
                {offer.discountType === 'percentage' ? `${offer.discountValue}% off` : `$${offer.discountValue} off`}
              </span>
            </div>

            <div className="grid grid-cols-2 gap-2 text-sm">
              {offer.triggerDays > 0 && (
                <div className="flex items-center gap-2">
                  <Clock className="h-3 w-3 text-gray-400" />
                  <span>{offer.triggerDays} days trigger</span>
                </div>
              )}
              {offer.visitThreshold > 0 && (
                <div className="flex items-center gap-2">
                  <Users className="h-3 w-3 text-gray-400" />
                  <span>{offer.visitThreshold} visits</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                {offer.deliveryMethod === 'email' ? <Mail className="h-3 w-3 text-gray-400" /> : <MessageSquare className="h-3 w-3 text-gray-400" />}
                <span className="capitalize">{offer.deliveryMethod}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-gray-400" />
                <span>Valid {offer.validityDays}d</span>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2 border-t">
              <Badge variant="secondary" className="text-xs">Sent: {offer.sentCount || 0}</Badge>
              <Badge variant="secondary" className="text-xs">Redeemed: {offer.redeemedCount || 0}</Badge>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" size="sm" onClick={() => handleEdit(offer)}>
                <Edit2 className="h-4 w-4 mr-1" />Edit
              </Button>
              <Button variant="ghost" size="sm" onClick={() => handleDelete(offer)} className="text-red-500 hover:text-red-700">
                <Trash2 className="h-4 w-4 mr-1" />Delete
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderTabContent = (type) => {
    const typeInfo = OFFER_TYPES[type];
    const offersList = offers[type] || [];
    const Icon = typeInfo.icon;
    
    return (
      <TabsContent value={type} className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Icon className={`h-5 w-5 text-${typeInfo.color}-500`} />
              {typeInfo.title}
            </h2>
            <p className="text-gray-500 text-sm">{typeInfo.description}</p>
          </div>
          <Button onClick={openNewForm} data-testid={`add-${type}-offer-btn`}>
            <Plus className="h-4 w-4 mr-2" />
            Create Offer
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : offersList.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="text-center py-12">
              <Zap className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No {typeInfo.title.toLowerCase()}</h3>
              <p className="text-gray-500 mb-4">Create your first automated {type} offer</p>
              <Button onClick={openNewForm}>
                <Plus className="h-4 w-4 mr-2" />Create Offer
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {offersList.map(renderOfferCard)}
          </div>
        )}
      </TabsContent>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-automated-offers">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Zap className="h-6 w-6 text-yellow-500" />
          Automated Offers
        </h1>
        <p className="text-gray-600">Set up automated marketing campaigns that trigger based on client behavior</p>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        {Object.entries(OFFER_TYPES).map(([type, info]) => {
          const Icon = info.icon;
          const count = (offers[type] || []).length;
          const active = (offers[type] || []).filter(o => o.active).length;
          return (
            <Card key={type}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{info.title}</p>
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-xs text-green-600">{active} active</p>
                  </div>
                  <Icon className={`h-8 w-8 text-${info.color}-500 opacity-50`} />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="retention" className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Retention
          </TabsTrigger>
          <TabsTrigger value="loyalty" className="gap-2">
            <Heart className="h-4 w-4" />
            Loyalty
          </TabsTrigger>
          <TabsTrigger value="rescue" className="gap-2">
            <Gift className="h-4 w-4" />
            Rescue
          </TabsTrigger>
          <TabsTrigger value="birthday" className="gap-2">
            <Cake className="h-4 w-4" />
            Birthday
          </TabsTrigger>
        </TabsList>

        {Object.keys(OFFER_TYPES).map(type => renderTabContent(type))}
      </Tabs>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>{editingOffer ? 'Edit Automated Offer' : 'Create Automated Offer'}</CardTitle>
                <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="oName">Offer Name *</Label>
                  <Input id="oName" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="e.g., 30-Day Win-Back" required className="mt-1" />
                </div>

                <div>
                  <Label htmlFor="oType">Offer Type</Label>
                  <select id="oType" value={formData.offerType} onChange={(e) => setFormData({...formData, offerType: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="retention">Retention Offer</option>
                    <option value="loyalty">Loyalty Offer</option>
                    <option value="rescue">Rescue Offer</option>
                    <option value="birthday">Birthday Offer</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="oDiscountValue">Discount Value *</Label>
                    <div className="relative mt-1">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <Input id="oDiscountValue" type="number" value={formData.discountValue} onChange={(e) => setFormData({...formData, discountValue: e.target.value})} placeholder="20" required className="pl-10" />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="oDiscountType">Discount Type</Label>
                    <select id="oDiscountType" value={formData.discountType} onChange={(e) => setFormData({...formData, discountType: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                      <option value="percentage">Percentage Off</option>
                      <option value="fixed">Fixed Amount</option>
                      <option value="credit">Account Credit</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="oTriggerDays">{getDefaultTriggerText(formData.offerType)}</Label>
                    <Input id="oTriggerDays" type="number" value={formData.triggerDays} onChange={(e) => setFormData({...formData, triggerDays: e.target.value})} placeholder="30" className="mt-1" />
                  </div>
                  {formData.offerType === 'loyalty' && (
                    <div>
                      <Label htmlFor="oVisits">Visit Threshold</Label>
                      <Input id="oVisits" type="number" value={formData.visitThreshold} onChange={(e) => setFormData({...formData, visitThreshold: e.target.value})} placeholder="5" className="mt-1" />
                    </div>
                  )}
                  <div>
                    <Label htmlFor="oValidity">Offer Valid (days)</Label>
                    <Input id="oValidity" type="number" value={formData.validityDays} onChange={(e) => setFormData({...formData, validityDays: e.target.value})} placeholder="30" className="mt-1" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="oDelivery">Delivery Method</Label>
                  <select id="oDelivery" value={formData.deliveryMethod} onChange={(e) => setFormData({...formData, deliveryMethod: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="email">Email Only</option>
                    <option value="sms">SMS Only</option>
                    <option value="both">Email & SMS</option>
                  </select>
                </div>

                <div>
                  <Label htmlFor="oLocation">Location</Label>
                  <select id="oLocation" value={formData.locationId} onChange={(e) => setFormData({...formData, locationId: e.target.value})} className="w-full mt-1 px-3 py-2 border rounded-lg">
                    <option value="">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label htmlFor="oMessage">Message Template</Label>
                  <Textarea id="oMessage" value={formData.messageTemplate} onChange={(e) => setFormData({...formData, messageTemplate: e.target.value})} placeholder="Hi {name}, we miss you! Here's {discount}% off your next visit..." rows={3} className="mt-1" />
                  <p className="text-xs text-gray-500 mt-1">Use {'{name}'}, {'{discount}'}, {'{expiry}'} as placeholders</p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.active} onChange={(e) => setFormData({...formData, active: e.target.checked})} className="rounded" />
                  <span className="text-sm">Active (starts sending immediately)</span>
                </label>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
                  <Button type="submit" disabled={saving}>
                    {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><Save className="h-4 w-4 mr-2" />{editingOffer ? 'Update' : 'Create'} Offer</>}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AdminAutomatedOffers;
